import type { MailSignature } from "../../services/mailSignatureService";

/**
 * Die Einträge des Signatur-Dropdowns in der Verfassen-Werkzeugleiste.
 *
 * Bis 09/2026 verschwand die Auswahl vollständig, solange es keine Signatur
 * gab (`{signatures.length > 0 && …}` im ComposeMailWindow). Wer noch keine
 * angelegt hatte, sah im Verfassen-Fenster keinerlei Hinweis darauf, dass es
 * Signaturen überhaupt gibt — der Weg dorthin führte nur über das Zahnrad der
 * Mailliste, das man erst einmal finden muss.
 *
 * Deshalb steht das Dropdown jetzt immer, und der letzte Eintrag führt in die
 * Mail-Einstellungen, Reiter „Signaturen". Seine Beschriftung richtet sich
 * danach, ob schon etwas da ist: ohne Bestand ist „erstellen" die ehrliche
 * Ansage, mit Bestand „verwalten" (dort wird auch bearbeitet und gelöscht).
 *
 * Pur gehalten, damit die Zusammenstellung ohne DOM prüfbar bleibt.
 */

/** Wert des Eintrags, der die Signatur-Verwaltung öffnet (kein Signatur-Id). */
export const SIGNATUR_VERWALTEN = "verwalten";

export type SignaturOption = {
    /** `<option value>`: die Signatur-Id als String oder {@link SIGNATUR_VERWALTEN}. */
    value: string;
    label: string;
};

export function signaturOptionen(signaturen: readonly MailSignature[]): SignaturOption[] {
    const eintraege: SignaturOption[] = signaturen.map((s) => ({
        value: String(s.id),
        label: s.isDefault ? `${s.name} (Standard)` : s.name,
    }));
    eintraege.push({
        value: SIGNATUR_VERWALTEN,
        label: eintraege.length === 0 ? "Signatur erstellen…" : "Signaturen verwalten…",
    });
    return eintraege;
}

/**
 * Übersetzt den gewählten Wert zurück: eine Signatur-Id oder „Verwaltung
 * öffnen". Ein unbekannter Wert (leere Auswahl, Fremdeingabe) liefert `null` —
 * der Aufrufer tut dann nichts.
 */
export function signaturAuswahl(
    value: string,
    signaturen: readonly MailSignature[],
): { art: "verwalten" } | { art: "einfuegen"; signatur: MailSignature } | null {
    if (value === SIGNATUR_VERWALTEN) return { art: "verwalten" };
    const id = Number(value);
    if (!Number.isFinite(id)) return null;
    const signatur = signaturen.find((s) => s.id === id);
    return signatur ? { art: "einfuegen", signatur } : null;
}
